import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

export const THEMES = Object.freeze({ LIGHT: 'light', DARK: 'dark' });

/** Same key the Remix reference uses, so a shared browser keeps one preference. */
export const THEME_STORAGE_KEY = 'houdiny.theme.v1';

const ThemeContext = createContext(null);

const isDarkValue = value => value === THEMES.DARK;

/** Read the persisted choice. Never throws — private mode can block storage. */
export const readStoredTheme = () => {
  try {
    return isDarkValue(window.localStorage.getItem(THEME_STORAGE_KEY)) ? THEMES.DARK : THEMES.LIGHT;
  } catch {
    return THEMES.LIGHT;
  }
};

/**
 * Class that switches off every CSS transition for the duration of a theme
 * swap. Without it the swap is not atomic: the app inherited a number of
 * `transition: all` rules with durations from 0.15s to 0.4s, so panels, the
 * header and the thread rows re-tint at different speeds and the change reads
 * as a smear rather than a flip. The reference has no cross-fade either — it
 * repaints in one frame — so suppressing is what matches it.
 */
const NO_TRANSITION_CLASS = 'theme-switching';

/**
 * The only place that touches the DOM. Sets three things:
 *  - `.dark` class      — parity with the reference's selector
 *  - `data-theme` attr  — what GlobalStyles keys the dark token block off
 *  - `color-scheme`     — makes native scrollbars, inputs and browser UI follow
 *                         the theme, which CSS variables alone cannot do
 */
export const applyThemeToDocument = theme => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const dark = isDarkValue(theme);

  root.classList.add(NO_TRANSITION_CLASS);
  root.classList.toggle('dark', dark);
  root.dataset.theme = dark ? THEMES.DARK : THEMES.LIGHT;
  root.style.colorScheme = dark ? 'dark' : 'light';

  // Reading a layout property flushes the new colours together with the
  // suppression class, so no transition can ever start against the old values.
  void root.offsetHeight;

  // Two frames: the first paints the new theme, the second restores hover and
  // interaction transitions once that paint is on screen.
  const restore = () => root.classList.remove(NO_TRANSITION_CLASS);
  if (typeof window === 'undefined' || !window.requestAnimationFrame) {
    restore();
    return;
  }
  window.requestAnimationFrame(() => window.requestAnimationFrame(restore));
};

export const ThemeProvider = ({ children }) => {
  // Initialised lazily from storage so the first render is already correct.
  const [theme, setThemeState] = useState(readStoredTheme);

  // Layout effect: applied before the browser paints, so no flash.
  useLayoutEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  // Keep other open tabs in sync with the choice made here.
  useEffect(() => {
    const onStorage = event => {
      if (event.key !== THEME_STORAGE_KEY) return;
      setThemeState(isDarkValue(event.newValue) ? THEMES.DARK : THEMES.LIGHT);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setTheme = useCallback(next => {
    const value = isDarkValue(next) ? THEMES.DARK : THEMES.LIGHT;
    setThemeState(value);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, value);
    } catch {
      /* preference stays session-only when storage is unavailable */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(isDarkValue(theme) ? THEMES.LIGHT : THEMES.DARK);
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme, isDark: isDarkValue(theme) }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

/** `const { theme, isDark, setTheme, toggleTheme } = useTheme();` */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return context;
};

export default ThemeProvider;
